import { Component, OnDestroy, Output } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { RenderingContext, RenderingContextProvider, SitePage } from './../../../api';
import {
  createSingleSubject,
  isNil,
  isNotNil,
  isString,
  opShareLast,
  pageFromRenderingContext,
  perfMeasure,
} from './../../../utils';
import { li } from './../../services/logger/logger.service';
import { WchService } from './../../services/wch.service';
import { pageSetActivePage } from './../../utils/page.utils';
import { AbstractBaseComponent } from './../rendering/abstract-base.component';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'PageComponent';

const _META_NAME_ID = 'id';
const _META_NAME_DESCRIPTION = 'description';
const _EMPTY_VALUE = '';

/*
 * Makes sure to return a string value
 *
 * @param aValue
 */
function _boxValue(aValue?: string): string {
  return isString(aValue) ? aValue : _EMPTY_VALUE;
}

/*
 * Sets the metadata tag
 *
 * @param aMetaService the service
 * @param aId the ID
 * @param aValue the value
*/
function _setMetaTag(aMetaService: Meta, aId: string, aValue?: string) {
  // dispatch
  aMetaService.updateTag({ name: aId, content: _boxValue(aValue) });
}

/*
 * Sets the title tag
 *
 * @param aMetaService the service
 * @param aValue the value
*/
function _setTitleTag(aTitle: Title, aValue?: string) {
  // dispatch
  aTitle.setTitle(_boxValue(aValue));
}

/*
 * Extracts the title
 *
 * @param aSitePage   the page
 * @param aRenderingContext   the context
 *
 * @return the title
 */
function _getTitle(
  aSitePage: SitePage,
  aRenderingContext: RenderingContext
): string {
  // extract the title
  let title = isNotNil(aSitePage)
    ? aSitePage.title || aSitePage.name
    : undefined;
  if (isNil(title) && isNotNil(aRenderingContext)) {
    title = aRenderingContext.name;
  }
  // the title
  return title;
}

/*
 * Extracts the description
 *
 * @param aSitePage   the page
 * @param aRenderingContext   the context
 *
 * @return the description
 */
function _getDescription(
  aSitePage: SitePage,
  aRenderingContext: RenderingContext
): string {
  // extract the title
  let desc = isNotNil(aSitePage) ? aSitePage.description : undefined;
  if (isNil(desc) && isNotNil(aRenderingContext)) {
    desc = aRenderingContext.description;
  }
  // the desc
  return desc;
}

/*
 * Updates page metadata with the info from the rendering context
 *
 * @param aContext  the context
 */
function _updateMetaData(
  aContext: RenderingContext,
  aTitleService: Title,
  aMetaService: Meta
) {
  // destroy
  li(LOGGER, 'updateMetaData', aContext);
  // handle the case that we don't know have a rendering context
  const page = pageFromRenderingContext(aContext);
  if (isNil(page)) {
    // remove
    _setTitleTag(aTitleService, _EMPTY_VALUE);
    _setMetaTag(aMetaService, _META_NAME_ID, _EMPTY_VALUE);
    _setMetaTag(aMetaService, _META_NAME_DESCRIPTION, _EMPTY_VALUE);
  } else {
    // update title
    _setTitleTag(aTitleService, _getTitle(page, aContext));
    _setMetaTag(aMetaService, _META_NAME_ID, page.id);
    _setMetaTag(
      aMetaService,
      _META_NAME_DESCRIPTION,
      _getDescription(page, aContext)
    );
  }
}

/**
 * Combines the active pieces of the state into one single object
 */
export declare type ComponentState = [RenderingContext, string];

/*
 * The page component monitors
 */
@Component({
  selector: 'wch-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.css'],
  preserveWhitespaces: false
})
export class PageComponent extends AbstractBaseComponent implements OnDestroy, RenderingContextProvider {
  /*
   * Fires whenever the layout mode changed, includes the initial default mode.
   */
  @Output() readonly onLayoutMode: Observable<string>;

  /*
   * The rendering context for the page. This is the context
   * of the element that is referenced by the currently active route.
   */
  @Output() readonly onRenderingContext: Observable<RenderingContext>;

  /*
   * Event that propagates the newly created component
   */
  @Output() readonly onComponent: ReplaySubject<any> = createSingleSubject();

  /**
   * The component state
   */
  readonly onState: Observable<ComponentState>;

  constructor(
    aRoute: ActivatedRoute,
    aWchService: WchService,
    aTitleService: Title,
    aMetaService: Meta
  ) {
    // default
    super();
    // measure startup
    const handle = perfMeasure(LOGGER);

    const that = this;

    // attach to the path
    const onRenderingContext = (this.onRenderingContext = aWchService
      .getRenderingContextByActivatedRoute(aRoute)
      .pipe(
        // update metadata
        tap<RenderingContext>(ctx =>
          _updateMetaData(ctx, aTitleService, aMetaService)
        ),
        // update the page, so e.g. the navigation can listen to it
        tap(pageSetActivePage),
        // cache the last value
        opShareLast,
        // end at the right point in time
        takeUntil(that.onOnDestroy)
      ));

    // attach to the mode
    const onLayoutMode = that.onLayoutMode;

    /**
     * Combines the latest changes
     */
    const onComponentState = (this.onState = combineLatest(
      onRenderingContext,
      onLayoutMode
    ));

    // done
    handle();
  }

  // just dispatch, otherwise the method will not be recognized
  ngOnDestroy() {
    super.ngOnDestroy();
  }
}
